/* Global UnreadContext — single source of truth for the in-app messaging
   badge. Mounted ONCE at the App root for every logged-in user.

   It does two things:
   1. Polls /api/messaging/unread-count once at startup + every 60s as a
      safety net for missed SSE events.
   2. Opens a single Server-Sent Events stream to /api/messaging/stream and
      bumps / refreshes the count whenever a `message:new` event arrives
      addressed to someone OTHER than the current user (i.e. they received
      something) — and decrements when `conversation:read` fires (multi-
      device sync).

   Consumers read the badge value with `useUnread()` and trigger a manual
   refresh after writing (e.g. opening a thread). */
import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { useUser } from "./UserAuthContext";
import { useMessagingStream } from "./MessagingStreamContext";
import { getUnreadCount } from "../lib/messagingApi";

const UnreadContext = createContext({ count: 0, refresh: () => {}, status: "idle" });

export function UnreadProvider({ children }) {
  const { user } = useUser();
  const [count, setCount] = useState(0);
  const [status, setStatus] = useState("idle");
  const myIdRef = useRef(null);

  const refresh = useCallback(async () => {
    try {
      const n = await getUnreadCount();
      setCount(n);
    } catch {
      /* keep current count on transient failure */
    }
  }, []);

  useEffect(() => {
    if (!user?.id) {
      setCount(0);
      setStatus("idle");
      return undefined;
    }
    myIdRef.current = user.id;
    setStatus("open");
    // initial sync + 60s safety poll
    refresh();
    const poll = setInterval(refresh, 60_000);
    return () => clearInterval(poll);
  }, [user?.id, refresh]);

  // Live SSE — shared stream from MessagingStreamProvider (NO extra
  // EventSource opened here; previously we opened our own which caused
  // 3 simultaneous SSE connections per user and slowed everything down).
  useMessagingStream((ev) => {
    if (!ev || !myIdRef.current) return;
    if (ev.type === "message:new" && ev.message?.sender_id !== myIdRef.current) {
      refresh();
      try {
        window.dispatchEvent(new CustomEvent("lexiq:message-new", { detail: ev }));
      } catch { /* noop */ }
    }
    if (ev.type === "conversation:read") {
      refresh();
    }
  });

  return (
    <UnreadContext.Provider value={{ count, refresh, status }}>
      {children}
    </UnreadContext.Provider>
  );
}

export const useUnread = () => useContext(UnreadContext);
